import React from "react";
import CommonStyles from "../InputFieldsController/InputFieldsController.module.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import {
  FieldProps,
  getFieldContainerStyle,
} from "../InputFieldsController/InputFieldsController.utils";
import LabeledInputWrapper from "../InputFieldsController/LabeledInputWrapper";

import {
  getDisplayFormat,
  toPickerDate,
  fromPickerDate,
  normalizeStoredDateForDisplay,
} from "./DateInput.utils";

/**
 * DateInput
 *
 * Renders a date or date-time picker (react-datepicker) that:
 * - Displays using the device's local time zone (no hard-coded EST/IST).
 * - Stores values in the field's chosen storage format:
 *    • Date-only: storageFormatDate (default "yyyy-MM-dd")
 *    • Date-time: storageFormatDateTime (default "yyyy-MM-dd'T'HH:mm:ssXXX")
 * - Reads back from that same storage format reliably.
 *
 * Configure formats per field via `field.dateConfig`:
 *   {
 *     displayFormatDate?: "MM/dd/yyyy",
 *     displayFormatDateTime?: "MM/dd/yyyy h:mm aa",
 *     storageFormatDate?: "yyyy-MM-dd",
 *     storageFormatDateTime?: "yyyy-MM-dd'T'HH:mm:ssXXX"
 *   }
 *
 * If you choose a date-time storage format without 'XXX', you drop the numeric offset by choice.
 */
interface DateInputProps {
  field: FieldProps;
  handleChange: (fieldId: string, event: any) => void;
  handleBlur: (fieldId: string) => void;
  handleSelect: (fieldId: string) => void;
}

const DateInput: React.FC<DateInputProps> = ({
  field,
  handleChange,
  handleBlur,
  handleSelect,
}) => {
  const {
    id,
    value,
    placeholder,
    example,
    disabled,
    isValid = true,
    error,
    show = true,
    showTimeSelect = false,
    selected,
    isDOB,
  } = field;

  const containerStyle = getFieldContainerStyle(field);
  const dateFormat = getDisplayFormat(field);

  const normalizedStored =
    typeof value === "string"
      ? normalizeStoredDateForDisplay(value, dateFormat)
      : value;

  const selectedDate = toPickerDate(field, normalizedStored);

  const today = new Date();
  const maxDate = isDOB ? today : undefined;
  const minDate = isDOB ? new Date(1900, 0, 1) : undefined;

  if (!show) return null;

  return (
    <div style={containerStyle}>
      <LabeledInputWrapper field={field}>
        <DatePicker
          key={`${id}-${showTimeSelect}-${dateFormat}`}
          id={id}
          selected={selectedDate}
          onChange={(picked: Date | null) => {
            const stored = fromPickerDate(field, picked);
            handleChange(id, stored ?? "");
          }}
          onBlur={() => handleBlur(id)}
          onFocus={() => handleSelect(id)}
          placeholderText={placeholder}
          disabled={disabled}
          showTimeSelect={showTimeSelect}
          timeIntervals={15}
          dateFormat={dateFormat}
          minDate={minDate}
          maxDate={maxDate}
          className={`form-control ${CommonStyles.customInput} ${
            selected ? CommonStyles.selectedField : ""
          } ${!isValid ? "is-invalid" : ""}`}
        />
        {!isValid && error && (
          <div className="invalid-feedback d-block">{error}</div>
        )}
      </LabeledInputWrapper>

      {example && <small className="text-muted">Example: {example}</small>}
    </div>
  );
};

export default DateInput;
